import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import MFAVerificationForm from '../components/MFA/MFAVerificationForm';
import MFASetupRequired from '../components/MFA/MFASetupRequired';
import './Login.css';

// Configure axios with the API base URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const axiosInstance = axios.create({
  baseURL: API_URL,
});

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  
  // MFA states
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [mfaSessionId, setMfaSessionId] = useState('');
  const [mfaUserInfo, setMfaUserInfo] = useState<any>(null);
  
  const { login } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Check if there's a success message from navigation state
    const state = location.state as { message?: string };
    if (state?.message) {
      setSuccessMessage(state.message);
      // Clear the message from navigation state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      // First, try to login directly through API to check for MFA
      const response = await axiosInstance.post('/auth/login', {
        username,
        password,
      });
      
      const data = response.data as any;
      
      if (data.mfa_setup_required) {
        // MFA setup is required - show forced setup
        setMfaSetupRequired(true);
        setMfaSessionId(data.setup_session_id);
        setMfaUserInfo(data.user);
      } else if (data.mfa_required) {
        // MFA required - show verification form
        setMfaRequired(true);
        setMfaSessionId(data.mfa_session_id);
        setMfaUserInfo(data.user);
      } else {
        // No MFA - use the regular login flow
        await login(username, password);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleMFACancel = () => {
    setMfaRequired(false);
    setMfaSetupRequired(false);
    setMfaSessionId('');
    setMfaUserInfo(null);
    setPassword('');
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetMessage('');
    setResetLoading(true);
    
    try {
      const response = await axiosInstance.post<{ message: string }>('/auth/forgot-password', {
        email: resetEmail
      });
      setResetMessage(response.data.message);
    } catch (err: any) {
      setResetMessage(err.response?.data?.message || 'Failed to send reset email');
    } finally {
      setResetLoading(false);
    }
  };

  if (mfaSetupRequired) {
    return (
      <MFASetupRequired 
        sessionId={mfaSessionId}
        userInfo={mfaUserInfo}
      />
    );
  }

  if (mfaRequired) {
    return (
      <MFAVerificationForm 
        sessionId={mfaSessionId}
        userInfo={mfaUserInfo}
        onCancel={handleMFACancel}
      />
    );
  }

  if (showForgotPassword) {
    return (
      <div className="login-container">
        <div className="login-header">
          <h1>ComplexLaw</h1>
          <h2>PCP Claims Management System</h2>
        </div>
        
        <div className="login-form-container">
          <div className="login-card">
            <h2>Reset Password</h2>
            
            {resetMessage && (
              <div className={resetMessage.includes('sent') ? 'success-message' : 'error-message'} 
                   style={{ 
                     color: resetMessage.includes('sent') ? '#4caf50' : '#f44336',
                     marginBottom: '1rem', 
                     padding: '0.75rem', 
                     backgroundColor: resetMessage.includes('sent') ? '#e8f5e9' : '#fff1f0',
                     borderRadius: '4px' 
                   }}>
                {resetMessage}
              </div>
            )}
            
            <form onSubmit={handleForgotPassword}>
              <div className="form-group">
                <label htmlFor="resetEmail">Email Address</label>
                <input
                  id="resetEmail"
                  type="email"
                  name="resetEmail"
                  value={resetEmail}
                  onChange={e => setResetEmail(e.target.value)}
                  required
                  placeholder="Enter your email address"
                />
              </div>
              
              <button 
                className="login-button" 
                type="submit"
                disabled={resetLoading}
              >
                {resetLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
              
              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetMessage('');
                    setError('');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#00A99D',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '0.9rem'
                  }}
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-header">
        <h1>ComplexLaw</h1>
        <h2>PCP Claims Management System</h2>
      </div>
      
      <div className="login-form-container">
        <div className="login-card">
          <h2>Sign In</h2>
          
          {successMessage && (
            <div className="success-message" style={{ color: '#4caf50', marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
              {successMessage}
            </div>
          )}
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                name="username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                name="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
            </div>
            
            <button 
              className="login-button" 
              type="submit"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(true);
                  setResetEmail(username); // Pre-fill with username if available
                  setError('');
                  setResetMessage('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#00A99D',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '0.9rem'
                }}
              >
                Forgot password?
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
